import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { finalize } from 'rxjs';
import {
  MbButtonComponent,
  MbInputComponent,
  MbLanguageSwitcherComponent,
  MbLogoComponent,
  ToastService,
} from '../../components/common';
import { AuthService } from '../../core/services';
import { ASSET_CONSTANTS } from '../../shared/constants/asset-constants';
import {
  RequestResetPasswordSchema,
  ResetPasswordSchema,
} from './forgot-password.schema';

type NotificationMethod = 'EMAIL' | 'SMS';

@Component({
  selector: 'app-forgot-password',
  standalone: true,
  imports: [
    MbLogoComponent,
    MbInputComponent,
    MbButtonComponent,
    ReactiveFormsModule,
    TranslocoDirective,
    MbLanguageSwitcherComponent,
  ],
  templateUrl: './forgot-password.component.html',
})
export class ForgotPasswordComponent {
  protected readonly ASSET_CONSTANTS = ASSET_CONSTANTS;
  private authService = inject(AuthService);
  private router = inject(Router);
  private toastService = inject(ToastService);
  private transloco = inject(TranslocoService);

  requestResetPasswordForm = new FormGroup({
    identifier: new FormControl(''),
  });

  resetPasswordForm = new FormGroup({
    code: new FormControl(''),
    newPassword: new FormControl(''),
  });

  zodErrors: Record<string, string> = {};

  isResetPassword = false;
  isRequestingReset = false;
  isResettingPassword = false;

  requestPasswordReset(): void {
    if (this.isRequestingReset) return;

    this.zodErrors = {};
    const formValue = this.requestResetPasswordForm.value;
    const zodResult = RequestResetPasswordSchema.safeParse(formValue);

    if (!zodResult.success) {
      this.applyZodErrors(zodResult.error.issues, this.requestResetPasswordForm);
      return;
    }

    const { identifier, notificationMethod } = this.resolveIdentifier(
      formValue.identifier ?? ''
    );

    this.isRequestingReset = true;
    this.authService
      .requestPasswordReset(identifier, notificationMethod)
      .pipe(finalize(() => (this.isRequestingReset = false)))
      .subscribe({
        next: (response) => {
          this.toastService.show(
            response.message || 'Password reset request sent successfully',
            'success'
          );
          this.isResetPassword = true;
        },
        error: (error) => {
          this.toastService.show(this.extractErrorMessage(error), 'error');
        },
      });
  }

  resetPassword(): void {
    if (this.isResettingPassword) return;

    this.zodErrors = {};
    const formValue = this.resetPasswordForm.value;
    const zodResult = ResetPasswordSchema.safeParse(formValue);

    if (!zodResult.success) {
      this.applyZodErrors(zodResult.error.issues, this.resetPasswordForm);
      return;
    }

    const { code, newPassword } = zodResult.data;

    this.isResettingPassword = true;
    this.authService
      .resetPassword(code, newPassword)
      .pipe(finalize(() => (this.isResettingPassword = false)))
      .subscribe({
        next: (response) => {
          this.toastService.show(
            response.message || 'Password reset successfully',
            'success'
          );
          this.router.navigate(['/auth']);
        },
        error: (error) => {
          this.toastService.show(this.extractErrorMessage(error), 'error');
        },
      });
  }

  navigateToLogin(): void {
    this.router.navigate(['/auth']);
  }

  /**
   * Same rule as the citizen portal: an identifier containing "@" is an email
   * and is notified by EMAIL; anything else is treated as a Rwandan phone
   * number, normalised to +2507XXXXXXXX and notified by SMS.
   */
  private resolveIdentifier(rawIdentifier: string): {
    identifier: string;
    notificationMethod: NotificationMethod;
  } {
    const compactIdentifier = rawIdentifier.trim().replace(/\s+/g, '');

    if (compactIdentifier.includes('@')) {
      return { identifier: compactIdentifier, notificationMethod: 'EMAIL' };
    }

    let identifier = compactIdentifier;
    if (identifier.startsWith('07')) {
      identifier = `+250${identifier.slice(1)}`;
    } else if (identifier.startsWith('2507')) {
      identifier = `+${identifier}`;
    }

    return { identifier, notificationMethod: 'SMS' };
  }

  private applyZodErrors(
    issues: { path: PropertyKey[]; message: string }[],
    form: FormGroup
  ): void {
    issues.forEach((issue) => {
      if (issue.path && issue.path.length > 0) {
        this.zodErrors[issue.path[0] as string] = this.transloco.translate(
          issue.message
        );
      }
    });
    Object.keys(form.controls).forEach((key) => form.get(key)?.markAsTouched());
  }

  private extractErrorMessage(error: {
    error?: { message?: string } | string;
    message?: string;
  }): string {
    if (typeof error?.error === 'string') return error.error;
    return error?.error?.message ?? error?.message ?? 'An error occurred';
  }
}
